import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import type { ActionError } from "@hyphesync/shared";
import { ApiError, SessionExpiredError, isAuth0SessionEnded, responseOutcome } from "./rules";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export { ApiError, SessionExpiredError } from "./rules";

/** Authenticated JSON calls. The access token comes from the Auth0 SDK (memory cache + rotating
 *  refresh token); the SPA never stores a token itself. */
export function useApi() {
  const { getAccessTokenSilently } = useAuth0();
  return useCallback(
    async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
      let token: string | undefined;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        if (isAuth0SessionEnded(e)) throw new SessionExpiredError("auth0");
        throw e;
      }
      if (!token) throw new SessionExpiredError("no_token");
      const res = await fetch(`${BASE}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const outcome = responseOutcome(res.status);
      if (outcome === "session_ended") throw new SessionExpiredError("api_401");
      if (outcome === "no_content") return undefined as T;
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new ApiError(res.status, data.error ?? "error");
      return data as T;
    },
    [getAccessTokenSilently],
  );
}

/** Wording for every admin-action refusal. Typed against the shared ACTION_ERRORS, so a code the
 *  API adds or renames fails typecheck here instead of reaching people as "Something went wrong". */
const ACTION_TEXT: Record<ActionError, string> = {
  last_admin: "A workspace needs at least one active admin.",
  cannot_modify_self: "You can't change your own role or status.",
  protected_user: "That person is a HypheSync super admin; they can't be demoted or disabled here.",
  invite_limit: "This workspace has sent its 50 invitations for the last 24 hours. Try again later.",
  connector_name_taken: "Another connector already has that name.",
  provider_unavailable: "That provider isn't available here yet.",
  credentials_missing: "Add credentials before turning this connector on.",
  connector_inactive: "Turn this connector on (or resume it) before running it.",
  test_cooldown: "Wait a few seconds before testing this connector again.",
  run_in_progress: "This connector is already running. Wait for that run to finish.",
  connector_changed: "Someone else just changed this connector. Check its current settings and try again.",
  credentials_unavailable: "Credentials can't be saved right now (the encryption key isn't configured). Contact HypheSync support.",
};
const OTHER_TEXT: Record<string, string> = {
  already_member: "That person is already a member.",
  invalid_request: "Check the form and try again.",
  Forbidden: "Your role can't do that.",
  "Not Found": "That item no longer exists. Reload the page.",
  "Too Many Requests": "Too many requests. Wait a minute and try again.",
  auth_unavailable: "Sign-in is temporarily unavailable. Try again in a minute.",
};

/** One-line, user-facing text for an API failure. */
export function describeError(e: unknown): string {
  if (e instanceof SessionExpiredError) return "Your session has ended. Reload the page to sign in again.";
  if (e instanceof ApiError) {
    const text = (ACTION_TEXT as Record<string, string>)[e.code] ?? OTHER_TEXT[e.code];
    return text ?? `Something went wrong (${e.status}). Try again.`;
  }
  return "Couldn't reach HypheSync. Check your connection and try again.";
}
